import type { Dispatch, SetStateAction } from "react";
import { useToast } from "@elaniintc/goes-design-system";
import type { useNavigate } from "react-router";
import type { useGetTableById } from "@/queries/data-center/data-center.api";
import { DataCenterConfirmationModal } from "./confirmation-modal";
import { ConfirmationModalBase } from "@/components/common/modals";
import NewColumnModal from "./NewColumnModal";
import EditTableModal from "./EditTableModal";
import EditColumnModal from "./EditColumnModal";
import { savedChangesToast } from "./detail.helpers";
import type { DetailModalState, SelectedColumn } from "./detail.types";

interface DetailModalsProps {
  modalState: DetailModalState;
  setModalState: Dispatch<SetStateAction<DetailModalState>>;
  onSubmit: () => Promise<void>;
  isLoadingSave: boolean;
  data: ReturnType<typeof useGetTableById>["data"];
  tableId: string;
  refetch: () => void;
  refetchTableData: () => void;
  showToast: ReturnType<typeof useToast>["showToast"];
  selectedColumn: SelectedColumn | null;
  setSelectedColumn: Dispatch<SetStateAction<SelectedColumn | null>>;
  isLoadingDeleteRowData: boolean;
  onDeleteRowsData: () => Promise<void>;
  navigate: ReturnType<typeof useNavigate>;
}

export const DetailModals = ({
  modalState,
  setModalState,
  onSubmit,
  isLoadingSave,
  data,
  tableId,
  refetch,
  refetchTableData,
  showToast,
  selectedColumn,
  setSelectedColumn,
  isLoadingDeleteRowData,
  onDeleteRowsData,
  navigate,
}: DetailModalsProps) => {
  return (
    <>
      {modalState === "save" && (
        <DataCenterConfirmationModal
          onClose={() => {
            setModalState("idle");
          }}
          onAccept={onSubmit}
          isLoading={isLoadingSave}
          isOpen={modalState === "save"}
          mode="save"
        />
      )}
      {modalState === "new-column" && (
        <NewColumnModal
          open={modalState === "new-column"}
          name={data?.data?.name || ""}
          description={data?.data?.description || ""}
          tableId={tableId}
          onSuccess={() => {
            setModalState("idle");
            refetch();
            refetchTableData();
            showToast(savedChangesToast);
          }}
          onClose={() => {
            setModalState("idle");
          }}
        />
      )}
      {modalState === "edit-column" && selectedColumn && (
        <EditColumnModal
          onClose={() => {
            setModalState("idle");
            setSelectedColumn(null);
          }}
          columns={data?.data?.columns || []}
          table={{
            id: data?.data?.id || "",
            name: data?.data?.name || "",
            description: data?.data?.description || "",
            table_display_name: data?.data?.table_display_name || "",
          }}
          onSuccess={() => {
            refetch();
            showToast(savedChangesToast);
            setModalState("idle");
            setSelectedColumn(null);
            refetchTableData();
          }}
          columnData={{
            id: selectedColumn.id,
            data_type: selectedColumn.data_type as
              "STRING" | "INTEGER" | "BOOLEAN",
            name: selectedColumn.name,
            display_name: selectedColumn.display_name || "",
          }}
          open={modalState === "edit-column"}
        />
      )}
      {modalState === "delete" && (
        <ConfirmationModalBase
          acceptText="Eliminar registros"
          cancelText="Cancelar"
          description="¿Estás seguro de que deseas eliminar los registros de la lista? Se eliminará la información de todas sus celdas."
          title="Eliminar registros"
          isOpen={modalState === "delete"}
          isLoading={isLoadingDeleteRowData}
          onAccept={onDeleteRowsData}
          onClose={() => {
            setModalState("idle");
          }}
          acceptVariant="danger-secondary"
        />
      )}
      {modalState === "edit-table" && (
        <EditTableModal
          tableData={{
            description: data?.data?.description || "",
            name: data?.data?.name || "",
            id: data?.data?.id || "",
            table_display_name: data?.data?.table_display_name || "",
            is_portal_enabled_for_creation:
              data?.data?.is_portal_enabled_for_creation || false,
          }}
          open={modalState === "edit-table"}
          onDeactivate={() => {
            navigate("/centro-de-datos");
          }}
          onSuccess={() => {
            refetch();
          }}
          onClose={() => {
            setModalState("idle");
          }}
        />
      )}
    </>
  );
};

export default DetailModals;
